import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { useOrganization } from "../context/OrganizationContext";
import "./NoOrganizationScreen.css";

/**
 * NoOrganizationScreen - Fallback UI for users with zero organizations
 * Requirements: 10.4, 10.5, 10.6, 10.7, 12.3, 12.4
 */
export function NoOrganizationScreen() {
  const { user, token } = useAuth();
  const { refreshOrganizations } = useOrganization();
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCreateOrganization = async () => {
    try {
      setIsCreating(true);
      setError(null);

      const response = await fetch('/api/organization/ensure-default', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to create organization');
      }

      // Refresh organizations to trigger re-initialization
      await refreshOrganizations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
      setIsCreating(false);
    }
  };

  return (
    <div className="no-org-screen">
      <div className="no-org-container">
        <div className="no-org-icon">
          <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        
        <h1 className="no-org-title">
          Welcome to Haven{user?.name ? `, ${user.name.split(' ')[0]}` : ''}!
        </h1>
        
        <p className="no-org-description">
          Let's create your personal workspace to get started.
        </p>

        {error && (
          <div className="no-org-error">
            <p className="no-org-error-message">{error}</p>
          </div>
        )}
        
        <div className="no-org-actions">
          <button
            onClick={handleCreateOrganization}
            className="no-org-button no-org-button-primary"
            disabled={isCreating}
          >
            {isCreating ? (
              <>
                <span className="no-org-spinner"></span>
                Creating...
              </>
            ) : (
              'Create My Organization'
            )}
          </button>
        </div>
        
        <div className="no-org-help">
          <p className="no-org-help-text">
            Need help? <a href="mailto:support@haven.app" className="no-org-help-link">Contact Support</a>
          </p>
        </div>
      </div>
    </div>
  );
}
